import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import api from "@/lib/api";
import AssemblyQuoteSheet from "@/components/AssemblyQuoteSheet";
import NpdQuoteSheet from "@/components/NpdQuoteSheet";

// Rasterise one rendered DOM node and append it to the shared jsPDF as A4 page(s).
async function addNodeAsPages(pdf, el, isFirst) {
  const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff", logging: false });
  const imgData = canvas.toDataURL("image/jpeg", 0.9);
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW;
  const imgH = (canvas.height * imgW) / canvas.width;
  let heightLeft = imgH;
  let position = 0;
  if (!isFirst) pdf.addPage();
  pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
  heightLeft -= pageH;
  while (heightLeft > 0) {
    position -= pageH;
    pdf.addPage();
    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
    heightLeft -= pageH;
  }
}

/**
 * One-click assembly quote → single PDF: the assembly quote first, then every child
 * (sub-part) NPD quote appended after it. Set `assemblyId` to trigger; it fetches the
 * assembly + its component NPD docs, renders each sheet off-screen, and downloads one PDF.
 */
export default function AssemblyPdfDownloader({ assemblyId, onDone }) {
  const [data, setData] = useState(null); // { asm, componentNpds, customers, settings, units }
  const asmRef = useRef(null);
  const childRefs = useRef([]);
  const ran = useRef(false);

  // 1) Load the assembly, its component NPDs, and reference data.
  useEffect(() => {
    if (!assemblyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, c, s, u] = await Promise.all([
          api.get(`/assemblies/${assemblyId}`),
          api.get("/customers").catch(() => ({ data: [] })),
          api.get("/settings/public").catch(() => ({ data: {} })),
          api.get("/units").catch(() => ({ data: [] })),
        ]);
        const ids = (a.data.components || []).map((cm) => cm.npd_id).filter(Boolean);
        let comp = [];
        if (ids.length) {
          comp = (await Promise.all(ids.map((nid) => {
            if (String(nid).startsWith("asm_")) {
              return api.get(`/assemblies/${nid}`).then((r) => ({
                npd_id: nid, _is_assembly: true,
                grand_total: r.data.grand_total,
                development_cost_total: r.data.development_cost_total,
                npd_number: r.data.assembly_number,
                part_name: r.data.assembly_name, part_number: "",
              })).catch(() => null);
            }
            return api.get(`/npd/${nid}`).then((r) => r.data).catch(() => null);
          }))).filter(Boolean);
        }
        if (!cancelled) setData({ asm: a.data, componentNpds: comp, customers: c.data || [], settings: s.data || {}, units: u.data || [] });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load assembly for PDF");
        onDone && onDone();
      }
    })();
    return () => { cancelled = true; };
  }, [assemblyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) Once the sheets are mounted + images loaded, build the combined PDF.
  useEffect(() => {
    if (!data || ran.current) return;
    ran.current = true;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 200)); // let the sheets mount
      const container = document.getElementById("asm-pdf-container");
      const deadline = Date.now() + 8000;
      while (!cancelled && Date.now() < deadline) {
        const imgs = Array.from(container?.querySelectorAll("img") || []);
        const ready = imgs.length === 0 || imgs.every((i) => i.complete && i.naturalWidth > 0);
        if (ready) break;
        await new Promise((r) => setTimeout(r, 150));
      }
      if (cancelled) return;
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        await addNodeAsPages(pdf, asmRef.current, true);
        for (let i = 0; i < data.componentNpds.length; i++) {
          const el = childRefs.current[i];
          if (el) await addNodeAsPages(pdf, el, false);
        }
        pdf.save(`${data.asm.assembly_number || "Assembly"}_quote.pdf`);
      } catch (e) {
        toast.error("Could not generate assembly PDF");
      } finally {
        if (!cancelled) onDone && onDone();
      }
    })();
    return () => { cancelled = true; };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!assemblyId) return null;
  const asm = data?.asm;
  const customerName = asm ? (data.customers.find((c) => c.customer_id === asm.customer_id)?.name || "—") : "—";
  const unit = asm ? (data.units.find((u) => u.unit_id === asm.unit_id) || null) : null;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 bg-slate-900 text-white text-xs rounded-md px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="asm-pdf-preparing-chip">
        <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        Preparing assembly PDF…
      </div>
      {data && (
        <div id="asm-pdf-container" style={{ position: "fixed", left: "-10000px", top: 0, width: "210mm", background: "#fff" }} aria-hidden>
          <div ref={asmRef} className="bg-white p-6">
            <AssemblyQuoteSheet asm={asm} componentNpds={data.componentNpds} customerName={customerName} settings={data.settings} unit={unit} />
          </div>
          {data.componentNpds.map((child, i) => {
            // Nested-assembly children have no NPD quote sheet — skip their appended page.
            if (child._is_assembly) { childRefs.current[i] = null; return null; }
            const cName = data.customers.find((c) => c.customer_id === child.customer_id)?.name || customerName;
            const cUnit = data.units.find((u) => u.unit_id === child.unit_id) || unit;
            return (
              <div key={child.npd_id || i} ref={(el) => (childRefs.current[i] = el)} className="bg-white p-6">
                <NpdQuoteSheet npd={child} customerName={cName} settings={data.settings} unit={cUnit} />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
